/** @jsx jsx */
import { useRef, useState, useEffect } from 'react'
import { bool, object } from 'prop-types'
import { node, string } from 'prop-types'
import { navigate } from 'gatsby'
import { Card, Text, Flex, jsx, Divider, Image } from 'theme-ui'
import StrategyIcons from './StrategyIcons'
import SocialMediaButton from './SocialMediaButton'
import SubSection from './SubSection'
import { WebsiteService } from '../../../icons'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { optionFeatureCard } from './richTextRenderFeatureCard'
import PropTypes from 'prop-types'

const FeatureCard = ({
  title,
  subTitle,
  descriptionRichText,
  icon,
  isFeatureVisible = true,
  entityName = '',
  isSocialMediaButton,
  subSectionButtonLink,
  subSectionButtonText,
  subSectionHeadline,
  mainSectionButtonText,
  mainSectionButtonLink,
  isRedirectContentLink,
  redirectSource,
  ...props
}) => {
  const isStrategyBuilder = entityName.toLowerCase() === 'strategy builder'
  const showSubSection = subSectionHeadline || subSectionButtonText
  const [encodedResponse, setEncodedResponse] = useState('')
  const [postUrl, setPostUrl] = useState('')
  const formRef = useRef(null)

  const handleDivClick = () => {
    if (isRedirectContentLink && formRef.current) {
      formRef.current.submit()
    }
  }

  const handleSubmit = event => {
    event.preventDefault()
  }
  useEffect(() => {
    if (isRedirectContentLink && redirectSource?.distribionSamlResponse) {
      const encodedRes = window.btoa(
        redirectSource?.distribionSamlResponse.samlResponse
      )
      setEncodedResponse(encodedRes)
      setPostUrl(redirectSource?.distribionSamlResponse.postUrl)
    }
  }, [redirectSource, isRedirectContentLink])

  return (
    <Card
      variant="feature"
      data-automation-id={`${title
        .toLowerCase()
        .replaceAll(' ', '-')}-feature-card`}
      sx={{
        minWidth: 268,
        mr: 0,
        height: '100%',
        transition: 'box-shadow 0.2s, border-color 0.2s',
        borderRight: '1px solid #979491',
        borderBottom: '1px solid #979491',
        background: '#FAFAFA',
        boxShadow: '0 2px 35px 0 rgba(28, 34, 38, 0.15)',
        '&:hover': {
          cursor: mainSectionButtonText ? 'default' : 'pointer',
          boxShadow: '3px 3px 10px 0 #6F6C69',
          borderBottom: '1px solid #6F6C69',
          borderRight: '1px solid #6F6C69',
        },
      }}
      {...props}
    >
      <form
        ref={formRef}
        method="post"
        action={postUrl}
        target="_blank"
        onSubmit={handleSubmit}
      >
        <div
          onClick={handleDivClick}
          role="button"
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') handleDivClick(e)
          }}
        >
          <Text
            as="input"
            type="text"
            id="SAMLResponse"
            name="SAMLResponse"
            value={encodedResponse}
            readOnly
            sx={{ display: 'none' }}
          ></Text>

          <Flex sx={{ justifyContent: 'flex-start', alignItems: 'flex-start' }}>
            <Flex sx={{ mt: '2px', width: 24, height: 24, flexShrink: 0 }}>
              <Image
                variant="cardIcon"
                src={icon}
                data-automation-id={`${title
                  .toLowerCase()
                  .replaceAll(' ', '-')}-feature-icon`}
                sx={{ width: '100%', height: '100%' }}
              />
            </Flex>
            <Text
              data-automation-id={`${title
                .toLowerCase()
                .replaceAll(' ', '-')}-title`}
              variant="h6"
              sx={{
                ml: 3,
                lineHeight: '22.5px',
                fontWeight: '450',
                fontFamily: 'Haptik',
                fontStyle: 'normal',
                display: 'flex',
                fontSize: '20px',
                alignItems: 'flex-start',
                color: 'var(--Primary-Black, #000)',
              }}
            >
              {title}
            </Text>
          </Flex>
          <Divider sx={{ width: '100%', my: 4, color: '#979491' }} />
          {descriptionRichText?.raw &&
            documentToReactComponents(
              JSON.parse(descriptionRichText.raw),
              optionFeatureCard
            )}
          <Text variant="xSmallText">
            {isFeatureVisible ? (
              subTitle
            ) : isStrategyBuilder ? (
              <span>
                <b>ATTENTION</b>: Strategy Builder is currently not supported on
                mobile devices. For a better user experience, you can access
                Strategy Builder from your desktop computer.
              </span>
            ) : (
              'Coming soon.'
            )}
          </Text>
          {!!mainSectionButtonText && (
            <Flex
              data-automation-id="main-section-button"
              sx={{
                display: 'inline-flex',
                width: 'auto',
                minWidth: 0,
                border: '1px solid #83928B',
                backgroundColor: '#D6DBD8',
                paddingY: '3px',
                paddingX: '11px',
                fontSize: '14px',
                fontWeight: 500,
                alignItems: 'center',
                color: 'black',
                mt: '11px',
                '&:hover': {
                  cursor: 'pointer',
                  boxShadow: '2px 2px 4px 0 #6F6C69',
                },
                svg: {
                  marginRight: '12px',
                },
              }}
              onClick={() => navigate(mainSectionButtonLink)}
            >
              <WebsiteService />
              {mainSectionButtonText}
            </Flex>
          )}
          {showSubSection && (
            <SubSection
              headline={subSectionHeadline}
              title={subSectionButtonText}
              link={subSectionButtonLink}
              data-automation-id="sub-section-button"
            />
          )}
        </div>
      </form>
      {isStrategyBuilder && <StrategyIcons />}
      {isSocialMediaButton && <SocialMediaButton />}
    </Card>
  )
}

FeatureCard.propTypes = {
  icon: node.isRequired,
  subTitle: string,
  description: object,
  descriptionRichText: PropTypes.shape({
    raw: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }),
  title: string.isRequired,
  isFeatureVisible: bool,
  entityName: string,
  isSocialMediaButton: bool,
  subSectionButtonLink: string,
  subSectionButtonText: string,
  subSectionHeadline: string,
  mainSectionButtonText: string,
  mainSectionButtonLink: string,
  isRedirectContentLink: bool,
  redirectSource: object,
}

export default FeatureCard
